import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { MdRestaurant } from "react-icons/md";
import { PiCertificateFill } from "react-icons/pi";
import { FaBirthdayCake } from "react-icons/fa";
import { FaHandshake } from "react-icons/fa";

const Banner = ({ banners }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % banners.length);
  }, [banners.length]);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? banners.length - 1 : prev - 1));
  }, [banners.length]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % banners.length);
    }, 10000);

    return () => clearInterval(interval);
  }, [banners.length]);

  const currentBanner = banners[currentIndex];

  const handleButtonClick = useCallback(() => {

    // Cumpleaños
    if (currentIndex === 0) {
      navigate("/#products");
      return;
    }

    // SENIAT
    if (currentIndex === 1) {
      window.open("https://seniatenlinea.seniat.gob.ve/tributos-software-facturacion/", "_blank", "noopener,noreferrer");
      return;
    }

    // Innova Food & Drinks
    if (currentIndex === 2) {
      window.open("https://food.innovaprosystem.com", "_blank", "noopener,noreferrer");
      return;
    }

    // Registro en Innova Partners
    navigate("/partners");
  }, [currentIndex, navigate]);

  const bannerIcon = useMemo(() => {
    if (currentIndex === 0) {
      return (
        <FaBirthdayCake 
          className="text-white inline-block ml-1 text-2xl lg:text-4xl align-middle"
          aria-hidden="true"
        />
      );
    }

    if (currentIndex === 1) {
      return (
        <PiCertificateFill
          className="text-white inline-block ml-1 text-2xl lg:text-4xl align-middle"
          aria-hidden="true"
        />
      );
    }

    // Innova Food & Drinks
    if (currentIndex === 2) {
      return (
        <MdRestaurant
          className="text-white inline-block ml-1 text-2xl lg:text-4xl align-middle"
          aria-hidden="true"
        />
      );
    }

    // Registro de Innova Partners
    return (
      <FaHandshake
        className="text-white inline-block ml-1 text-2xl lg:text-4xl align-middle"
        aria-hidden="true"
      />
    );
  }, [currentIndex]);

  const isFirstSlide = currentIndex === 0;

  return (
    <section className="gradientBg rounded-xl rounded-br-[80px] md:p-9 px-4 py-9 relative overflow-hidden">
      <button
        type="button"
        onClick={goToPrev}
        className="absolute top-1/2 left-1 transform -translate-y-1/2 text-white text-2xl z-10"
        aria-label="Banner anterior"
      >
        <FaArrowLeft aria-hidden="true" />
      </button>

      <button
        type="button"
        onClick={goToNext}
        className="absolute top-1/2 right-1 transform -translate-y-1/2 text-white text-2xl z-10"
        aria-label="Banner siguiente"
      >
        <FaArrowRight aria-hidden="true" />
      </button>

      <div className="flex flex-col md:flex-row-reverse justify-between items-center gap-10">
        <div className="w-full md:w-2/5 flex justify-center">
          <img
            src={currentBanner.banner}
            alt={typeof currentBanner.heading === "string" ? currentBanner.heading : "Banner principal"}
            className="w-full max-w-[600px] lg:h-[386px] object-contain"
            width="600"
            height="386"
            loading={isFirstSlide ? "eager" : "lazy"}
            fetchPriority={isFirstSlide ? "high" : "auto"}
            decoding="async"
          />
        </div>

        <div className="md:w-3/5">
          <h1 className="md:text-7xl text-4xl font-bold text-white mb-6 leading-tight">
            <span>{currentBanner.heading}</span>
            {bannerIcon}
          </h1>

          <p className="text-white text-xl md:text-2xl mb-8">
            {currentBanner.subheading}
          </p>

          <div className="flex flex-col md:flex-row items-center space-y-4 md:space-x-5 md:space-y-0">
            <button type="button" className="btnPrimary" onClick={handleButtonClick}>
              {currentBanner.btn2}
            </button>
          </div>
        </div>
      </div>

      <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-3 z-10">
        {banners.map((banner, index) => (
          <button
            key={banner.id ?? `indicator-${index}`}
            type="button"
            onClick={() => setCurrentIndex(index)}
            className="flex h-10 w-10 items-center justify-center rounded-full"
            aria-label={`Ir al banner ${index + 1}`}
            aria-pressed={currentIndex === index}
          >
            <span
              className={`block h-3 w-3 rounded-full transition ${
                currentIndex === index ? "bg-white" : "bg-gray-400"
              }`}
            />
          </button>
        ))}
      </div>
    </section>
  );
};

export default Banner;